import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';

const HERO = '/images/CASES/HERO/HERO-CASES.webp';
const CHEGADA_ROTOR = '/images/CASES/id-fan-caldeira-recuperacao/GALERIA/CHEGADA-DO-ROTOR-DO-VENTILADOR-REPARADO.webp';
const TIME_ALINHANDO = '/images/CASES/id-fan-caldeira-recuperacao/GALERIA/TIME-ALINHANDO-VENTILADOR.webp';
const SUBIDA_ROTOR = '/images/CASES/id-fan-caldeira-recuperacao/GALERIA/SUBIDA-DO-ROTOR-REPARADO.webp';
const SUBIDA_CARCACA = '/images/CASES/id-fan-caldeira-recuperacao/GALERIA/SUBIDA-DA-CARCACA-DO-VENTILADOR.webp';
const RETIRADA_ROTOR = '/images/CASES/id-fan-caldeira-recuperacao/GALERIA/RETIRADA-DO-ROTOR-DO-VENTILADOR.webp';
const INSTALACAO_ROTOR = '/images/CASES/id-fan-caldeira-recuperacao/GALERIA/INSTALACAO-DO-ROTOR-NA-BASE.webp';
const DESCIDA_CARCACA = '/images/CASES/id-fan-caldeira-recuperacao/GALERIA/DESCIDA-DA-CARCACA-DO-VENTILADOR.webp';
const ALINHAMENTO = '/images/CASES/id-fan-caldeira-recuperacao/GALERIA/ALINHAMENTO-DO-VENTILADOR.webp';

export default function CaseDesmontagemMontagemIdFanPage() {
  useSeo({
    title: 'Desmontagem de Ventilador ID Fan | Case de Manutenção Industrial | Pilar',
    description: 'Conheça como a Pilar executou a desmontagem, retirada do rotor, reinstalação e alinhamento de um ventilador ID Fan de caldeira de recuperação em planta de papel e celulose.',
    keywords: 'ventilador ID Fan, caldeira de recuperação, ventilador industrial, manutenção mecânica industrial, desmontagem industrial, montagem industrial, alinhamento de equipamentos rotativos, papel e celulose, parada industrial, Grupo Pilar, Aracruz, ES',
    canonical: 'https://www.pilarmanutencao.com.br/cases/desmontagem-montagem-ventilador-id-fan-caldeira-recuperacao',
    ogUrl: 'https://www.pilarmanutencao.com.br/cases/desmontagem-montagem-ventilador-id-fan-caldeira-recuperacao',
    ogImage: HERO,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={HERO}
              alt="Rotor de ID Fan industrial recuperado e retornando para montagem após execução dos serviços especializados"
              title="Rotor de ID Fan recuperado — Case Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-4xl">
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span className="inline-block px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wider bg-gp-blue text-white">
                  Serviços de Campo
                </span>
                <span className="text-gray-400 text-xs uppercase tracking-wider">Papel e Celulose</span>
                <span className="text-gray-500 text-xs">— Aracruz, ES</span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Case:</span>{' '}
                Recuperação de ID Fan de Caldeira Industrial
              </h1>
              <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
                Execução completa da recuperação de um ID Fan de caldeira industrial, incluindo desmontagem, retirada do rotor, recuperação especializada, remontagem, alinhamento e retorno à operação.
              </p>
            </div>
          </div>
        </section>

        {/* Visão Geral */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Visão Geral</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-information-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Dados do Projeto</h3>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-settings-3-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Equipamento</span>
                        <p className="text-gray-300 text-sm">Ventilador ID Fan de Caldeira de Recuperação</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-team-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Equipe</span>
                        <p className="text-gray-300 text-sm">Aproximadamente 10 profissionais</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-sun-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Regime</span>
                        <p className="text-gray-300 text-sm">Dois turnos</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Prazo</span>
                        <p className="text-gray-300 text-sm">Aproximadamente 5 dias</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-building-2-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Segmento</span>
                        <p className="text-gray-300 text-sm">Papel e Celulose</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src={TIME_ALINHANDO}
                    alt="Equipe técnica da Pilar executando alinhamento final do conjunto após recuperação do equipamento"
                    title="Equipe Pilar executando alinhamento final do ID Fan"
                    className="w-full h-80 md:h-[28rem] object-contain bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Equipe técnica da Pilar executando alinhamento final do conjunto após recuperação do equipamento
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Desmontagem */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Desmontagem</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-alert-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-gray-300 text-base leading-relaxed mb-4">
                        O ventilador ID Fan é um equipamento rotativo crítico da caldeira de recuperação de uma planta de papel e celulose. A necessidade de recuperação do rotor exigia uma intervenção completa de desmontagem e montagem com janela de parada extremamente reduzida.
                      </p>
                      <p className="text-gray-400 text-sm leading-relaxed mb-4">
                        As principais complexidades envolviam:
                      </p>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Içamento de cargas pesadas com segurança operacional</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Trabalho em altura com protocolos rigorosos de segurança</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Equipamento rotativo crítico para a continuidade operacional</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Janela de parada reduzida — prazo de aproximadamente 5 dias</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Planejamento rigoroso com integração entre frentes de trabalho</span>
                        </li>
                      </ul>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        O principal desafio era executar a desmontagem completa do ventilador, retirar o rotor com segurança para envio à recuperação externa, reinstalar o rotor após retorno e garantir o alinhamento mecânico final — tudo dentro da janela de parada programada.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src={DESCIDA_CARCACA}
                    alt="Início das atividades com remoção controlada da carcaça para acesso aos componentes internos"
                    title="Descida da carcaça do ventilador ID Fan"
                    className="w-full h-80 md:h-[28rem] object-contain bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Início das atividades com remoção controlada da carcaça para acesso aos componentes internos
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Retirada do Rotor */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Retirada do Rotor</h2>
              </div>
              <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter mb-8">
                <img
                  src={RETIRADA_ROTOR}
                  alt="Retirada do rotor para inspeção, avaliação técnica e execução dos reparos necessários"
                  title="Retirada do rotor do ventilador ID Fan"
                  className="w-full h-80 md:h-[32rem] object-contain bg-gp-graphite"
                  loading="lazy"
                />
                <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                  <p className="text-gray-400 text-xs text-center">
                    Retirada do rotor para inspeção, avaliação técnica e execução dos reparos necessários
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Recuperação e Montagem */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Recuperação e Montagem</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src={SUBIDA_ROTOR}
                    alt="Rotor recuperado retornando à área de montagem após conclusão dos serviços especializados"
                    title="Subida do rotor reparado do ID Fan"
                    className="w-full h-80 md:h-[28rem] object-contain bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Rotor recuperado retornando à área de montagem após conclusão dos serviços especializados
                    </p>
                  </div>
                </div>

                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src={INSTALACAO_ROTOR}
                    alt="Instalação do rotor recuperado durante processo de remontagem do ventilador industrial"
                    title="Instalação do rotor na base do ID Fan"
                    className="w-full h-80 md:h-[28rem] object-contain bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Instalação do rotor recuperado durante processo de remontagem do ventilador industrial
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">01</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Planejamento detalhado da atividade com definição de escopo, recursos e cronograma</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">02</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Mobilização da equipe com aproximadamente 10 profissionais em dois turnos</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">03</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Desmontagem completa do ventilador ID Fan com instalação de bloqueios mecânicos de segurança</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">04</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Retirada do rotor com içamento controlado de cargas pesadas e preparação para transporte</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">05</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Apoio à logística de envio do rotor para recuperação externa especializada</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">06</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Recebimento do rotor recuperado e reinstalação no conjunto do ventilador</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">07</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Montagem completa do equipamento com todos os componentes periféricos</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">08</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Alinhamento mecânico final do conjunto rotativo com instrumentação de precisão</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">09</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Apoio à liberação para retorno operacional da caldeira de recuperação</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Fechamento e Alinhamento */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Fechamento e Alinhamento Final</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src={SUBIDA_CARCACA}
                    alt="Reposicionamento da carcaça após conclusão das atividades internas de recuperação"
                    title="Subida da carcaça do ventilador"
                    className="w-full h-80 md:h-[28rem] object-contain bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Reposicionamento da carcaça após conclusão das atividades internas de recuperação
                    </p>
                  </div>
                </div>

                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src={ALINHAMENTO}
                    alt="Alinhamento mecânico final para garantir desempenho, confiabilidade e vida útil operacional"
                    title="Alinhamento mecânico final do ID Fan"
                    className="w-full h-80 md:h-[28rem] object-contain bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Alinhamento mecânico final para garantir desempenho, confiabilidade e vida útil operacional
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <span className="text-gp-yellow font-display font-bold text-sm">FASE 1</span>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm uppercase mb-1">Planejamento e Mobilização</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">Definição do escopo, dimensionamento da equipe, planejamento de içamentos, elaboração de análise de riscos e mobilização de aproximadamente 10 profissionais em regime de dois turnos.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <span className="text-gp-yellow font-display font-bold text-sm">FASE 2</span>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm uppercase mb-1">Desmontagem e Retirada do Rotor</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">Desmontagem completa do ventilador ID Fan com bloqueios mecânicos de segurança, retirada do rotor com içamento controlado e preparação para envio à recuperação externa especializada.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <span className="text-gp-yellow font-display font-bold text-sm">FASE 3</span>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm uppercase mb-1">Apoio Logístico à Recuperação Externa</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">Suporte à logística de transporte do rotor para empresa externa especializada e recebimento do componente após recuperação, garantindo integridade durante todo o processo.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <span className="text-gp-yellow font-display font-bold text-sm">FASE 4</span>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm uppercase mb-1">Reinstalação e Montagem</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">Reinstalação do rotor recuperado e montagem completa do ventilador ID Fan com todos os componentes periféricos, seguindo especificações técnicas do fabricante.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <span className="text-gp-yellow font-display font-bold text-sm">FASE 5</span>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm uppercase mb-1">Alinhamento e Liberação</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">Alinhamento mecânico final com instrumentação de precisão, verificação de todos os parâmetros operacionais e apoio à liberação para retorno operacional da caldeira de recuperação.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Resultado Obtido */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Resultado Final</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="rounded-lg overflow-hidden border border-gp-yellow/20">
                  <img
                    src={TIME_ALINHANDO}
                    alt="Conclusão da recuperação do ID Fan industrial com equipe executando os ajustes finais antes da liberação para operação"
                    title="Resultado final da recuperação do ID Fan"
                    className="w-full h-80 md:h-[28rem] object-contain bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Conclusão da recuperação do ID Fan industrial com equipe executando os ajustes finais antes da liberação para operação
                    </p>
                  </div>
                </div>

                <div className="relative rounded-lg overflow-hidden border border-gp-yellow/20 bg-gp-graphite p-6 md:p-8">
                  <div className="absolute top-0 left-0 w-1 h-full bg-gp-yellow"></div>
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-trophy-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-white uppercase mb-2">Resultados</h3>
                    </div>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Prazo Cumprido</span>
                        <p className="text-gray-400 text-sm">Desmontagem, retirada do rotor, reinstalação e alinhamento executados dentro da janela de aproximadamente 5 dias</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-tools-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Desmontagem Completa</span>
                        <p className="text-gray-400 text-sm">Desmontagem total do ventilador ID Fan com retirada segura do rotor para recuperação externa</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-truck-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Logística Integrada</span>
                        <p className="text-gray-400 text-sm">Apoio completo à logística de envio e recebimento do rotor entre a planta e a empresa de recuperação externa</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-compass-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Alinhamento de Precisão</span>
                        <p className="text-gray-400 text-sm">Alinhamento mecânico final do conjunto rotativo com instrumentação de precisão</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Segurança</span>
                        <p className="text-gray-400 text-sm">Zero acidentes durante içamento de cargas pesadas e trabalho em altura</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-settings-3-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Retorno Operacional</span>
                        <p className="text-gray-400 text-sm">Caldeira de recuperação liberada para operação dentro do cronograma previsto</p>
                      </div>
                    </li>
                  </ul>
                  <div className="mt-6 pt-4 border-t border-gp-graphite-lighter bg-gp-graphite/40 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-information-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <p className="text-gray-400 text-xs leading-relaxed">
                        Importante: A recuperação do rotor foi executada por empresa externa especializada. O escopo da Pilar concentrou-se na desmontagem, retirada, apoio logístico, reinstalação, montagem e alinhamento mecânico — garantindo a integridade do equipamento e o cumprimento do prazo da parada.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Diferenciais Técnicos da Pilar */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Diferenciais Técnicos da Pilar</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-2-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Experiência comprovada em desmontagem e montagem industrial em plantas de papel e celulose</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Planejamento rigoroso de paradas industriais com janelas de tempo reduzidas</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-up-down-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Capacidade técnica em içamento de cargas pesadas com segurança operacional</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-tools-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Domínio em montagem e desmontagem de equipamentos rotativos críticos</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-compass-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Alinhamento mecânico de precisão com instrumentação especializada</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Integração entre frentes de trabalho com coordenação multidisciplinar em campo</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Galeria Técnica */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Galeria Técnica</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Foto 1 */}
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <div className="h-56 md:h-64 bg-gp-graphite flex items-center justify-center">
                    <img
                      src={CHEGADA_ROTOR}
                      alt="Rotor do ID Fan recuperado chegando para montagem — içamento com guindaste na área industrial"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">Rotor recuperado chegando para montagem — içamento com guindaste</p>
                  </div>
                </div>
                {/* Foto 2 */}
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <div className="h-56 md:h-64 bg-gp-graphite flex items-center justify-center">
                    <img
                      src={DESCIDA_CARCACA}
                      alt="Descida controlada da carcaça do ventilador ID Fan para início da desmontagem"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">Descida controlada da carcaça — início da desmontagem</p>
                  </div>
                </div>
                {/* Foto 3 */}
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <div className="h-56 md:h-64 bg-gp-graphite flex items-center justify-center">
                    <img
                      src={RETIRADA_ROTOR}
                      alt="Retirada do rotor com içamento controlado e equipe de segurança posicionada"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">Retirada do rotor com içamento controlado</p>
                  </div>
                </div>
                {/* Foto 4 */}
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <div className="h-56 md:h-64 bg-gp-graphite flex items-center justify-center">
                    <img
                      src={SUBIDA_ROTOR}
                      alt="Subida do rotor reparado de volta à estrutura do ID Fan para remontagem"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">Subida do rotor reparado para remontagem</p>
                  </div>
                </div>
                {/* Foto 5 */}
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <div className="h-56 md:h-64 bg-gp-graphite flex items-center justify-center">
                    <img
                      src={INSTALACAO_ROTOR}
                      alt="Instalação do rotor recuperado na base do ventilador durante remontagem"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">Instalação do rotor recuperado na base</p>
                  </div>
                </div>
                {/* Foto 6 */}
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <div className="h-56 md:h-64 bg-gp-graphite flex items-center justify-center">
                    <img
                      src={SUBIDA_CARCACA}
                      alt="Subida e reposicionamento da carcaça do ventilador após montagem interna concluída"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">Reposicionamento da carcaça após montagem interna</p>
                  </div>
                </div>
                {/* Foto 7 */}
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <div className="h-56 md:h-64 bg-gp-graphite flex items-center justify-center">
                    <img
                      src={ALINHAMENTO}
                      alt="Execução do alinhamento mecânico com instrumentação de precisão — etapa crítica antes da liberação"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">Alinhamento mecânico com instrumentação de precisão</p>
                  </div>
                </div>
                {/* Foto 8 */}
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <div className="h-56 md:h-64 bg-gp-graphite flex items-center justify-center">
                    <img
                      src={TIME_ALINHANDO}
                      alt="Equipe técnica Pilar realizando alinhamento final e ajustes antes da liberação do ID Fan"
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">Equipe Pilar realizando ajustes finais antes da liberação</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Serviços Relacionados */}
        <section className="relative py-16 overflow-hidden border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-lg font-bold text-white uppercase tracking-wider">Serviços Relacionados</h2>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/mecanica-industrial"
                  className="inline-flex items-center gap-2 bg-gp-graphite-light border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Mecânica Industrial
                </Link>
                <Link
                  to="/caldeiraria-solda"
                  className="inline-flex items-center gap-2 bg-gp-graphite-light border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Caldeiraria e Solda
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Solicite uma Avaliação Técnica
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Entre em contato com a Pilar e receba um diagnóstico especializado para sua operação de manutenção industrial. Especialistas em desmontagem, montagem e alinhamento de equipamentos rotativos em plantas de papel e celulose.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="https://wa.me/5527993092922"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                  >
                    <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <span>Solicitar Orçamento</span>
                    <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                  </a>
                  <Link
                    to="/contato"
                    className="inline-flex items-center justify-center gap-2 border border-gray-500 hover:border-gp-yellow text-gray-300 hover:text-gp-yellow px-8 py-4 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-mail-line text-lg w-6 h-6 flex items-center justify-center"></i>
                    Falar com Especialista
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Cases */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/cases"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para Cases
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-home-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}